import { useState } from 'react';
import styles from './MasteryHeatmap.module.css';

export interface Student {
  id: string;
  name: string;
}

export interface Concept {
  id: string;
  name: string;
}

export type MasteryData = Map<string, Map<string, number>>;

export interface MasteryHeatmapProps {
  students: Student[];
  concepts: Concept[];
  masteryData: MasteryData;
}

function getMasteryColor(score: number): string {
  if (score < 40) {
    const intensity = Math.min(1, score / 40);
    return `rgba(248, 81, 73, ${0.3 + intensity * 0.5})`;
  }
  if (score < 70) {
    const intensity = (score - 40) / 30;
    return `rgba(210, 153, 34, ${0.3 + intensity * 0.5})`;
  }
  const intensity = (score - 70) / 30;
  return `rgba(63, 185, 80, ${0.3 + intensity * 0.5})`;
}

export function MasteryHeatmap({ students, concepts, masteryData }: MasteryHeatmapProps) {
  const [hoveredCell, setHoveredCell] = useState<{ studentId: string; conceptId: string } | null>(null);

  const getScore = (studentId: string, conceptId: string): number => {
    const studentData = masteryData.get(studentId);
    return studentData?.get(conceptId) ?? 0;
  };

  const handleCellMouseEnter = (studentId: string, conceptId: string) => {
    setHoveredCell({ studentId, conceptId });
  };

  const handleCellMouseLeave = () => {
    setHoveredCell(null);
  };

  const getTooltipPosition = () => {
    if (!hoveredCell) return { display: 'none' };
    return { display: 'block' };
  };

  return (
    <div className={styles.heatmapContainer}>
      <div className={styles.scrollWrapper}>
        <table className={styles.heatmapTable}>
          <thead>
            <tr>
              <th className={styles.rowHeader}>Student</th>
              {concepts.map(concept => (
                <th key={concept.id} className={styles.columnHeader}>
                  <div className={styles.columnHeaderContent}>{concept.name}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {students.map(student => (
              <tr key={student.id}>
                <td className={styles.rowHeader}>
                  <div className={styles.rowHeaderContent}>{student.name}</div>
                </td>
                {concepts.map(concept => {
                  const score = getScore(student.id, concept.id);
                  const backgroundColor = getMasteryColor(score);

                  return (
                    <td
                      key={`${student.id}-${concept.id}`}
                      className={styles.cell}
                      style={{ backgroundColor }}
                      onMouseEnter={() => handleCellMouseEnter(student.id, concept.id)}
                      onMouseLeave={handleCellMouseLeave}
                    >
                      <span className={styles.cellScore}>{Math.round(score)}%</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tooltip */}
      {hoveredCell && (
        <div
          className={styles.tooltip}
          style={getTooltipPosition()}
        >
          <div className={styles.tooltipContent}>
            <span className={styles.tooltipStudent}>
              {students.find(s => s.id === hoveredCell.studentId)?.name}
            </span>
            <span className={styles.tooltipConcept}>
              {concepts.find(c => c.id === hoveredCell.conceptId)?.name}
            </span>
            <span className={styles.tooltipScore}>
              Mastery: {Math.round(getScore(hoveredCell.studentId, hoveredCell.conceptId))}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
